import { useAtomValue } from 'jotai';
import { useContext, useEffect, useRef } from 'react';
import type { SidePanelCommandContext } from '../../types';
import { SidePanelOwnerContext } from '../contexts/SidePanelOwnerContext';
import { surfaceOwnerIdAtom } from '../state/atoms';
import { useInternalSidePanelSystem } from './useInternalSidePanelSystem';

export type SidePanelCommandHandler<TInput = unknown, TOutput = unknown> = (
    input: TInput,
    context: SidePanelCommandContext
) => TOutput | Promise<TOutput>;

/**
 * Registers an owner-command handler on the nearest owner, falling back to the registered page surface.
 *
 * @typeParam TInput - Command input shape.
 * @typeParam TOutput - Handler result shape, including asynchronous results.
 * @param commandName - Command name to register within this owner.
 * @param handler - Latest callback invoked with command input and the dispatching session's context.
 * @remarks
 * Requires SidePanelSystemProvider. Registration waits until an owner or surface is available and is removed
 * when the registration effect cleans up. Updating the callback does not require re-registering it.
 */
export function useSidePanelCommandHandler<TInput = unknown, TOutput = unknown>(
    commandName: string,
    handler: SidePanelCommandHandler<TInput, TOutput>
) {
    const owner = useContext(SidePanelOwnerContext);
    const { commandHandlers, store } = useInternalSidePanelSystem();
    const surfaceOwnerId = useAtomValue(surfaceOwnerIdAtom, { store });
    const ownerId = owner?.ownerId ?? surfaceOwnerId;
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!ownerId) {
            return;
        }
        let handlers = commandHandlers.get(ownerId);
        if (!handlers) {
            handlers = new Map();
            commandHandlers.set(ownerId, handlers);
        }
        handlers.set(commandName, (input, context) =>
            handlerRef.current(input as TInput, context as SidePanelCommandContext)
        );

        return () => {
            handlers?.delete(commandName);
            if (handlers?.size === 0) {
                commandHandlers.delete(ownerId);
            }
        };
    }, [commandHandlers, commandName, ownerId]);
}
